<script>
import BaseModal from '@/components/_base-modal.vue'
import VuetifyTextClass from '@/shared/constants/core/VuetifyTextClass'
import dateMixin from '@/mixins/dateMixin'
import baseMixin from '@/mixins/baseMixin'
import ExtendBookingDTO from '@/models/bookings/extend-booking/extendBookingDTO'
import { mapActions, mapGetters } from 'vuex'
import ExtendBookingSummaryItem from '@/models/bookings/extend-booking/extendBookingSummaryItem.js'
import DateFormatToken from '@/shared/constants/date/DateFormatToken'
import VuetifyColourClass from '@/shared/constants/core/VuetifyColourClass'
import toasts from '@/services/toasts/index'
import ExtendErrorCodes from '@/shared/constants/error/ExtendBookingErrorCodes'
import CandidateAvailabilityDTO from '@/models/bookings/extend-booking/candidateAvailabilityDTO'
import ExtendBookingSummary from './extend-booking-summary.vue'
import ExtendBookingDatePicker from './extend-booking-date-picker.vue'
import ExtendBookingConfirmation from './extend-booking-confirmation.vue'

const { YYYYMMDD, dddDMMMYYYY, HHmm } = DateFormatToken

export default {
  components: {
    BaseModal,
    ExtendBookingDatePicker,
    ExtendBookingSummary,
    ExtendBookingConfirmation,
  },
  mixins: [baseMixin, dateMixin],
  model: {
    prop: 'show',
    event: 'input',
  },
  props: {
    booking: {
      type: Object,
      required: true,
    },
    isUnfilled: {
      type: Boolean,
      required: true,
    },
    show: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      ...VuetifyTextClass,
      grey600Text: VuetifyColourClass.text.grey600,
      extendedEndDate: '',
      isGoingBack: false,
      isCancelOpen: false,
      extendedBookings: [],
      candidateOptions: [],
      summaryBookings: [],
      clientSchedule: { defaultTimes: [] },
      extraValidation: {},
      summaryWarning: '',
      errorCode: null,
      validationMessage: '',
      width: {
        datePicker: 664,
        summary: 1100,
        confirmation: 1100,
      },
      height: {
        datePicker: 238,
        default: 680,
      },

      warningKeys: {
        invalidTimes: 'booking.extend.invalidTimesStartEndWarning',
        invalidStartTime: 'booking.extend.invalidStartTimeWarning',
        invalidEndTime: 'booking.extend.invalidEndTimeWarning',
      },
    }
  },
  computed: {
    ...mapGetters({
      isLoading: 'bookings/isLoadingExtendBooking',
    }),
    isOpen: {
      get() {
        return this.show
      },
      set(val) {
        if (!val) {
          this.handleClose()
          return
        }
        this.$emit('input', val)
      },
    },
    isFormInvalid() {
      return (
        !this.isTimesValid ||
        !this.isStartBeforeEnd ||
        !this.isEndAfterStart ||
        !this.isAfterMinStartTime ||
        !this.isLongerThanMinimumDuration
      )
    },
    confirmedBookings() {
      return this.summaryBookings.filter((item) => !item.removed)
    },
    endDate() {
      return (
        this.booking?.blockDetails?.endDateLocal ||
        this.booking.endDateTimeLocal
      )
    },
    extendStartDate() {
      if (this.candidateOptions?.length) {
        const startDate = this.candidateOptions[0].unavailability[0].dateLocal
        return this.formatDate(startDate, DateFormatToken.YYYYMMDD)
      }

      const today = this.getTodaysDate()

      let date = this.isBefore(this.endDate, today) ? today : this.endDate

      date = this.dateAdd(date, 1, this.durationUnits.DAY)

      date = this.formatDate(date, DateFormatToken.YYYYMMDD)

      return date || null
    },
    isExtendedEndDateValid() {
      if (!this.isValidDate(this.extendedEndDate)) return false
      const now = this.getTodaysDate()

      return (
        this.isAfter(this.extendedEndDate, this.endDate) &&
        this.isAfter(this.extendedEndDate, now)
      )
    },

    // #region Screen State
    isDatePickerScreen() {
      return !this.summaryBookings.length && !this.extendedBookings.length
    },
    isSummaryScreen() {
      return !!this.summaryBookings.length && !this.extendedBookings.length
    },
    isConfirmationScreen() {
      return !!this.extendedBookings.length
    },

    disableNext() {
      return !this.isExtendedEndDateValid || this.isFormInvalid
    },
    // #endregion

    // #region Modal UI
    primaryButtonText() {
      if (this.isDatePickerScreen) return this.$t('general.next')
      if (this.isSummaryScreen) return this.$t('booking.extend.extend')
      return this.$t('general.close')
    },
    modalWidth() {
      const { datePicker, summary, confirmation } = this.width
      return this.isDatePickerScreen
        ? datePicker
        : this.isSummaryScreen
        ? summary
        : confirmation
    },
    modalMaxHeight() {
      if (!this.isMediumOrHigher) return '100%'

      const { datePicker, default: defaultHeight } = this.height
      return this.isDatePickerScreen ? datePicker : defaultHeight
    },
    transitionName() {
      if (this.isGoingBack) return 'fast-fade'
      if (this.isConfirmationScreen) return 'fade-up'
      return ''
    },
    transitionMode() {
      return this.isGoingBack || this.isConfirmationScreen ? 'out-in' : ''
    },
    // #endregion

    // #region Candidate Helpers
    topPriorityCandidate() {
      return this.candidateOptions?.[0] || null
    },
    isTopPriorityCandidateUnavailable() {
      if (!this.topPriorityCandidate) return false

      return this.confirmedBookings.some((booking) => {
        const unavailabilityForDay = this.topPriorityCandidate.unavailability.find(
          (day) => this.isSame(day.dateLocal, booking.dateMeta)
        )

        if (!unavailabilityForDay || !unavailabilityForDay.times.length) {
          return false
        }

        return this.isTimeUnavailable(
          [unavailabilityForDay],
          booking.startTime,
          booking.endTime
        )
      })
    },
    // #endregion

    // #region Time Validation
    isTimesValid() {
      return this.confirmedBookings.every(
        ({ startTime, endTime }) =>
          this.isValidTime(startTime) && this.isValidTime(endTime)
      )
    },
    isStartBeforeEnd() {
      return this.confirmedBookings.every(({ startTime, endTime }) =>
        this.isBefore(startTime, endTime, this.durationUnits.MINUTE)
      )
    },
    isEndAfterStart() {
      return this.confirmedBookings.every(({ startTime, endTime }) =>
        this.isAfter(endTime, startTime, this.durationUnits.MINUTE)
      )
    },
    isAfterMinStartTime() {
      if (!this.extraValidation?.earliestStartTime24hr) return true

      return this.confirmedBookings.every(({ startTime }) =>
        this.isAfter(
          startTime,
          this.extraValidation.earliestStartTime24hr,
          this.durationUnits.MINUTE
        )
      )
    },
    isLongerThanMinimumDuration() {
      if (!this.extraValidation?.minimumDurationInMins) return true

      return this.confirmedBookings.every(
        ({ startTime, endTime }) =>
          this.diff(endTime, startTime, this.durationUnits.MINUTE) >
          this.extraValidation.minimumDurationInMins
      )
    },
    // #endregion

    // #region Warning/Info Alert
    showWarning() {
      return (
        this.isTopPriorityCandidateUnavailable ||
        this.isAnyTimeDifferentToSchedule ||
        this.isAnyBookingClosedInSchedule ||
        this.isFormInvalid ||
        this.errorCode
      )
    },
    warningMessage() {
      const {
        invalidTimes,
        invalidStartTime,
        invalidEndTime,
      } = this.warningKeys

      if (!this.showWarning) return ''
      if (!this.isTimesValid) return this.$t(invalidTimes)
      if (!this.isAfterMinStartTime) return this.startTimeBeforeMinWarning
      if (!this.isStartBeforeEnd) return this.$t(invalidStartTime)
      if (!this.isEndAfterStart) return this.$t(invalidEndTime)
      if (!this.isLongerThanMinimumDuration) return this.minDurationWarning
      if (this.isAnyBookingClosedInSchedule) return this.scheduleClosureWarning

      if (this.isAnyTimeDifferentToSchedule) {
        return this.$t('booking.extend.outsideWorkScheduleTimesWarning')
      }

      return this.isUnfilled && !this.candidateOptions.length
        ? ''
        : this.getUnavailableDatesWarning
    },
    minDurationWarning() {
      const hours = Math.floor(this.extraValidation.minimumDurationInMins / 60)
      return this.$t('booking.extend.invalidTimesMinDurationWarning', [
        `${hours} ${hours === 1 ? 'hour' : 'hours'}`,
      ])
    },
    startTimeBeforeMinWarning() {
      return this.$t('booking.extend.startTimeBeforeMinWarning', {
        time: this.formatDate(
          this.normalizeToDayjs(this.extraValidation.earliestStartTime24hr),
          DateFormatToken.hhmma
        ),
      })
    },
    scheduleClosureWarning() {
      const openDaysWithClosure = this.confirmedBookings.filter((booking) =>
        this.isDateClosedInSchedule(booking.dateMeta)
      )

      return this.$tc(
        'booking.extend.dayMarkedAsClosedInScheduleWarning',
        openDaysWithClosure.length
      )
    },
    getUnavailableDatesWarning() {
      const { name: candidateName = '', unavailability = [] } =
        this.topPriorityCandidate || {}
      const unavailableDates = this.confirmedBookings
        .filter(({ startTime, endTime, dateMeta }) => {
          const unavailableTimesForDay = unavailability.filter(
            ({ dateLocal }) => this.isSame(dateLocal, dateMeta)
          )
          return this.isTimeUnavailable(
            unavailableTimesForDay,
            startTime,
            endTime
          )
        })
        .map(({ dateMeta }) => dateMeta)

      const formattedUnavailableDates = this.formatDateRanges(
        unavailableDates,
        DateFormatToken.DoMMM,
        true
      )

      if (!candidateName || !formattedUnavailableDates?.length) {
        return !this.isUnfilled ? this.$t('booking.extend.genericWarning') : ''
      }

      const firstDateIsRange = formattedUnavailableDates[0].includes('-')
      const preposition = firstDateIsRange ? 'from' : 'on'
      const formattedDates = formattedUnavailableDates.join(', ')

      return this.$t('booking.extend.candidateUnavailableWarningWithDates', {
        candidateName,
        preposition,
        dates: formattedDates,
      })
    },
    // #endregion

    // #region Change Times Button
    showChangeTimesButton() {
      return this.shouldChangeTimes || this.shouldChangeRemoved
    },
    shouldChangeRemoved() {
      // single booking can't be removed
      const singleBooking = this.summaryBookings.length <= 1

      if (singleBooking || !this.scheduleHasOpenDays) return false

      return this.isAnyBookingClosedInSchedule
    },
    shouldChangeTimes() {
      if (
        this.isAnyTimeDifferentToDefault &&
        !this.areDefaultTimesSameAsSchedule
      )
        return true

      if (this.canChangeToValidScheduleTime) return true

      return false
    },

    changeTimesButtonText() {
      return this.canChangeToValidScheduleTime || this.shouldChangeRemoved
        ? this.$t('booking.extend.changeTimesToSchedule')
        : this.$t('booking.extend.changeTimesToBooking')
    },
    // #endregion

    // #region Schedule Comparison Helpers
    isAnyTimeDifferentToSchedule() {
      return this.confirmedBookings.some((booking) => {
        const isDifferent = this.areTimesDifferentToSchedule(booking)
        return !!isDifferent
      })
    },
    isAnyBookingClosedInSchedule() {
      return this.confirmedBookings.some((booking) => {
        const daySchedule = this.clientSchedule?.defaultTimes?.find((d) =>
          this.isSame(d.dateLocal, booking.dateMeta)
        )

        return daySchedule?.isClosed && !booking.removed
      })
    },
    defaultScheduleTimes() {
      return this.clientSchedule?.defaultTimes?.find((day) => !day.isClosed)
    },
    isAnyTimeDifferentToDefault() {
      return this.summaryBookings.some((booking) => {
        const defaultStart = this.formatDate(
          this.booking.startDateTimeLocal,
          HHmm
        )
        const defaultEnd = this.formatDate(this.booking.endDateTimeLocal, HHmm)

        return (
          !this.isSame(booking.startTime, defaultStart) ||
          !this.isSame(booking.endTime, defaultEnd)
        )
      })
    },
    canChangeToValidScheduleTime() {
      return this.scheduleHasOpenDays && this.isAnyTimeDifferentToSchedule
    },
    scheduleHasOpenDays() {
      return this.clientSchedule?.defaultTimes?.some((day) => !day.isClosed)
    },
    areDefaultTimesSameAsSchedule() {
      const defaultStart = this.formatDate(
        this.booking.startDateTimeLocal,
        HHmm
      )
      const defaultEnd = this.formatDate(this.booking.endDateTimeLocal, HHmm)

      return this.clientSchedule?.defaultTimes?.every(
        (d) =>
          this.isSame(d.startTime, defaultStart) &&
          this.isSame(d.endTime, defaultEnd)
      )
    },
    // #endregion
  },
  watch: {
    confirmedBookings: {
      handler() {
        this.summaryWarning = this.warningMessage
      },
      deep: true,
    },
  },
  methods: {
    ...mapActions({
      submitExtendBooking: 'bookings/extendBooking',
      getExtendBookingCandidatesAndSchedule:
        'bookings/getExtendBookingCandidatesAndSchedule',
    }),
    resetData() {
      this.extendedEndDate = null
      this.summaryWarning = ''
      this.summaryBookings = []
      this.extendedBookings = []
      this.extraValidation = {}
      this.clientSchedule = { defaultTimes: [] }
      this.candidateOptions = []
      this.isCancelOpen = false
      this.errorCode = null
    },

    // #region Navigation
    goBack() {
      this.isGoingBack = true
      if (this.isSummaryScreen) {
        this.summaryWarning = ''
        this.summaryBookings = []
      } else if (this.isConfirmationScreen) {
        this.extendedBookings = []
      }
      setTimeout(() => (this.isGoingBack = false), 200)
    },

    handleClose() {
      if (this.isConfirmationScreen) this.$store.commit('bookings/REFRESH_HOME')
      this.resetData()
      this.$emit('input', false)
    },
    handleNext() {
      if (this.isDatePickerScreen) {
        this.initSummary()
      } else if (this.isSummaryScreen) {
        this.extendBooking()
      } else {
        this.handleClose()
      }
    },
    // #endregion

    // #region Init & Submit
    async initSummary(warningMessage = null) {
      await this.getCandidateOptions()
      const summaryBookings = this.createSummaryBookings()

      const noOpenBookings = summaryBookings.every(({ removed }) => removed)

      if (noOpenBookings) summaryBookings[0].removed = false

      this.summaryBookings = summaryBookings

      this.$nextTick(() => {
        this.summaryWarning = warningMessage || this.warningMessage
      })
    },
    async getCandidateOptions() {
      const { data, error } = await this.getExtendBookingCandidatesAndSchedule({
        bookingId: this.booking.id,
        endDateLocal: this.extendedEndDate,
      })

      if (error) {
        this.handleClose()
        return
      }
      // BE returns sorted by priority
      this.candidateOptions = data.candidates.map(
        (candidate) => new CandidateAvailabilityDTO(candidate)
      )

      this.clientSchedule = data.clientSchedule || { defaultTimes: [] }

      this.extraValidation = data?.extraValidation || {
        minimumDurationInMins: 60,
        earliestStartTime24hr: '00:00',
      }
    },
    async extendBooking() {
      const payload = {
        days: this.confirmedBookings.map(
          ({ dateMeta, startTime, endTime, candidateId }) =>
            new ExtendBookingDTO(
              this.formatDate(dateMeta, YYYYMMDD),
              startTime,
              endTime,
              candidateId
            )
        ),
      }

      const { data, error } = await this.submitExtendBooking({
        bookingId: this.booking.id,
        payload,
      })

      if (error) {
        await this.handleExtendBookingError(error)
        return
      }

      this.errorCode = null
      this.extendedBookings = data.bookings
    },
    async handleExtendBookingError(error) {
      const { code } = error.data || {}
      this.errorCode = code

      switch (code) {
        case ExtendErrorCodes.EXTEND_GROUP_BOOKINGS_CANDIDATE_UNAVAILABLE:
          this.initSummary(
            this.$t('booking.extend.candidateBecameUnavailableWarning')
          )

          break
        case ExtendErrorCodes.EXTEND_GROUP_BOOKING_GROUP_RANGE_TOO_LARGE:
          this.summaryWarning = this.$t(
            'booking.extend.vicTeachingRangeLimitWarning'
          )
          break
        default:
          toasts.error(this.$t('error.genericApiError'))
      }
    },
    // #endregion

    // #region Form Field Updates
    setTime({ value, id, field }) {
      const booking = this.summaryBookings.find((item) => item.id === id)

      Object.assign(booking, { [field]: value })

      const isValidTime =
        this.isValidTime(booking.startTime) &&
        this.isValidTime(booking.endTime) &&
        this.isBefore(
          booking.startTime,
          booking.endTime,
          this.durationUnits.MINUTE
        )

      const validCandidate = this.findAvailableCandidate(
        booking.dateMeta,
        booking.startTime,
        booking.endTime,
        booking.candidateId
      )

      if (isValidTime) {
        Object.assign(booking, {
          candidateId: validCandidate.id,
          name: validCandidate.name,
        })
      }

      this.summaryWarning = this.warningMessage
    },
    setCandidateForBooking({ id, candidateId, name }) {
      const booking = this.summaryBookings.find((item) => item.id === id)
      if (!booking) return

      Object.assign(booking, { candidateId, name })
    },
    // #endregion

    // #region Change Times Button
    toggleScheduledTimes() {
      if (this.canChangeToValidScheduleTime || this.shouldChangeRemoved) {
        this.changeToScheduledTimes()
      } else {
        this.changeToDefaultTimes()
      }
    },
    changeToScheduledTimes() {
      const firstOpenBookingDate = this.confirmedBookings[0]?.dateMeta

      this.summaryBookings.forEach((booking) => {
        const scheduleForDay = this.clientSchedule?.defaultTimes?.find((d) =>
          this.isSame(d.dateLocal, booking.dateMeta)
        )

        const scheduleTimes = scheduleForDay?.isClosed
          ? this.defaultScheduleTimes
          : scheduleForDay

        const { startTime, endTime } = scheduleTimes || {
          startTime: booking.startTime,
          endTime: booking.endTime,
        }

        const { id: candidateId, name } = booking.candidateId
          ? this.findAvailableCandidate(
              booking.dateMeta,
              startTime,
              endTime,
              booking.candidateId
            )
          : { id: null, name: 'Unfilled' }

        Object.assign(booking, {
          startTime,
          endTime,
          removed: scheduleForDay?.isClosed,
          candidateId,
          name,
        })
      })

      // If all bookings are removed, ensure at least one remains open
      if (this.confirmedBookings.length === 0 && firstOpenBookingDate) {
        const bookingToReopen = this.summaryBookings.find((item) =>
          this.isSame(item.dateMeta, firstOpenBookingDate)
        )

        if (bookingToReopen) bookingToReopen.removed = false
      }
    },
    changeToDefaultTimes() {
      this.summaryBookings.forEach((booking) => {
        const startTime = this.formatDate(this.booking.startDateTimeLocal, HHmm)
        const endTime = this.formatDate(this.booking.endDateTimeLocal, HHmm)

        const { id: candidateId, name } = booking.candidateId
          ? this.findAvailableCandidate(
              booking.dateMeta,
              startTime,
              endTime,
              booking.candidateId
            )
          : { id: null, name: 'Unfilled' }

        Object.assign(booking, {
          startTime,
          endTime,
          candidateId,
          name,
        })
      })
    },
    // #endregion

    // #region Create Summary
    createSummaryBookings() {
      if (!this.extendStartDate) return []
      const dateDifferenceOffset = 1
      const diffDays =
        this.diff(
          this.extendedEndDate,
          this.extendStartDate,
          this.durationUnits.DAY
        ) + dateDifferenceOffset

      return Array.from({ length: diffDays }, (_, index) => {
        const newDate = this.dateAdd(
          this.extendStartDate,
          index,
          this.durationUnits.DAY
        )

        const existing =
          this.summaryBookings?.find((b) => this.isSame(b.dateMeta, newDate)) ??
          null

        return this.generateSummaryItem(this.extendStartDate, index, existing)
      })
    },
    generateSummaryItem(startDate, index, existing = null) {
      const newDate = this.dateAdd(startDate, index, this.durationUnits.DAY)
      const { startDateTimeLocal, endDateTimeLocal } = this.booking
      let { startTime, endTime } = existing || {}

      startTime = startTime ?? this.formatDate(startDateTimeLocal, HHmm)
      endTime = endTime ?? this.formatDate(endDateTimeLocal, HHmm)

      const { id: candidateId, name } = this.findAvailableCandidate(
        newDate,
        startTime,
        endTime,
        existing?.candidateId
      )

      const scheduleForDay = this.clientSchedule.defaultTimes.find((time) =>
        this.isSame(time.dateLocal, newDate)
      )

      return new ExtendBookingSummaryItem(
        newDate,
        this.formatDate(newDate, dddDMMMYYYY),
        startTime,
        endTime,
        candidateId,
        name,
        existing?.removed ?? scheduleForDay?.isClosed
      )
    },
    // #endregion

    // #region Availability
    findAvailableCandidate(newDate, startTime, endTime, preferred = null) {
      const unfilledCandidate = {
        id: null,
        name: 'Unfilled',
      }

      if (!this.candidateOptions?.length) return unfilledCandidate

      const preferredCandidate =
        this.candidateOptions.find(
          (candidate) =>
            candidate.id === preferred &&
            this.isCandidateAvailable(candidate, newDate, startTime, endTime)
        ) ?? null

      // If the error code means selected candidate has become unavailable, move to unfilled
      if (
        this.errorCode ===
          ExtendErrorCodes.EXTEND_GROUP_BOOKINGS_CANDIDATE_UNAVAILABLE &&
        !preferredCandidate
      ) {
        return unfilledCandidate
      }

      // Try to use existing data or top priority candidate
      if (preferredCandidate) return preferredCandidate

      // Otherwise, find the first available candidate
      const availableCandidate = this.candidateOptions.find((candidate) =>
        this.isCandidateAvailable(candidate, newDate, startTime, endTime)
      )

      return availableCandidate || unfilledCandidate
    },
    isCandidateAvailable(candidate, newDate, startTime, endTime) {
      const day = candidate.unavailability.find((unavailability) =>
        this.isSame(unavailability.dateLocal, newDate)
      )

      return !this.isTimeUnavailable([day], startTime, endTime) ?? false
    },
    isTimeUnavailable(unavailability, startTime, endTime) {
      return unavailability?.some((day) =>
        day?.times?.some((block) => {
          return (
            this.isBefore(
              startTime,
              block.endTime,
              this.durationUnits.MINUTE
            ) &&
            this.isBefore(block.startTime, endTime, this.durationUnits.MINUTE)
          )
        })
      )
    },
    // #endregion

    // #region Schedule Comparisons
    areTimesDifferentToSchedule(booking) {
      const daySchedule = this.clientSchedule?.defaultTimes?.find((d) =>
        this.isSame(d.dateLocal, booking.dateMeta)
      )

      if (booking.removed || !daySchedule) return false

      const startTime = daySchedule.isClosed
        ? this.defaultScheduleTimes?.startTime
        : daySchedule.startTime

      const endTime = daySchedule.isClosed
        ? this.defaultScheduleTimes?.endTime
        : daySchedule.endTime

      if (!startTime || !endTime) return false

      const isSameStart = this.isSame(booking.startTime, startTime)

      const isSameEnd = this.isSame(booking.endTime, endTime)

      if (isSameStart && isSameEnd) return false

      // return Object here to know which field to highlight
      return {
        date: booking.dateMeta,
        field: !isSameStart ? 'startTime' : !isSameEnd ? 'endTime' : null,
      }
    },
    isDateClosedInSchedule(date) {
      const daySchedule = this.clientSchedule?.defaultTimes?.find((d) =>
        this.isSame(d.dateLocal, date)
      )

      return daySchedule?.isClosed ?? false
    },
    // #endregion
  },
}
</script>

<template>
  <div>
    <BaseModal
      v-model="isOpen"
      :loading="isLoading"
      hide-header-divider
      :width="modalWidth"
      :max-height="modalMaxHeight"
      hide-cancel
      :persistent="true"
    >
      <template v-slot:header>
        <p :class="h6" class="mb-0">{{ $t('booking.extend.extendBooking') }}</p>
      </template>
      <template>
        <transition :name="transitionName" :mode="transitionMode">
          <ExtendBookingDatePicker
            v-if="isDatePickerScreen"
            v-model="extendedEndDate"
            :booking="booking"
            :min-allowed-date="extendStartDate"
          />
          <ExtendBookingSummary
            v-else-if="isSummaryScreen"
            v-model="summaryBookings"
            :candidate-options="candidateOptions"
            :confirmed-bookings="confirmedBookings"
            :unavailable-warning="summaryWarning"
            :extra-validation="extraValidation"
            :is-any-booking-closed-in-schedule="isAnyBookingClosedInSchedule"
            :is-date-closed-in-schedule="isDateClosedInSchedule"
            :are-times-different-to-schedule="areTimesDifferentToSchedule"
            :show-change-times-button="showChangeTimesButton"
            :change-times-button-text="changeTimesButtonText"
            :schedule-closure-warning="scheduleClosureWarning"
            @toggleScheduledTimes="toggleScheduledTimes"
            @setCandidate="setCandidateForBooking"
            @setTime="setTime"
          />
          <ExtendBookingConfirmation
            v-else
            :booking="booking"
            :extended-bookings="extendedBookings"
            :confirmed-bookings="confirmedBookings"
          />
        </transition>
      </template>
      <template v-slot:actions>
        <BaseButton
          v-if="isSummaryScreen"
          :class="smallBody"
          xs
          text
          @click="goBack"
        >
          {{ $t('general.back') }}
        </BaseButton>

        <v-spacer></v-spacer>

        <BaseButton
          v-if="!isConfirmationScreen"
          sm
          secondary
          width="83"
          height="41"
          border-radius="8px"
          @click="isCancelOpen = true"
        >
          {{ $t('general.cancel') }}
        </BaseButton>

        <BaseButton
          :disabled="disableNext"
          sm
          :width="isSummaryScreen ? 80 : 64"
          height="41"
          border-radius="8px"
          :loading="isLoading"
          @click="handleNext"
        >
          {{ primaryButtonText }}
        </BaseButton>
      </template>
    </BaseModal>
    <BaseModal
      v-model="isCancelOpen"
      hide-header
      hide-header-divider
      hide-cancel
      hide-actions-divider
      hide-actions
      width="430"
    >
      <template>
        <p :class="h6" class="mt-4 mb-2 text-center">{{
          $t('booking.extend.cancelModalHeader')
        }}</p>
        <p :class="[smallBody, grey600Text]" class="text-center mb-8">{{
          $t('booking.extend.cancelModalBody')
        }}</p>

        <div class="w-100 d-flex justify-space-around">
          <BaseButton
            :class="smallBody"
            secondary
            :disabled="isGoingBack"
            border-radius="8px"
            width="180"
            @click="isCancelOpen = false"
          >
            {{ $t('booking.extend.cancelModalCancel') }}
          </BaseButton>
          <BaseButton
            border-radius="8px"
            width="180"
            :disabled="isGoingBack"
            @click="handleClose"
          >
            {{ $t('booking.extend.cancelModalConfirm') }}
          </BaseButton>
        </div>
      </template>
    </BaseModal>
  </div>
</template>

<style lang="scss">
@import '@/styles/_vue-transitions.scss';

.align-unfilled-icon {
  margin-left: -2px !important;
}
</style>
